/**
 * Privacy Governance Panel
 * PROJ-082 - Privacy Governance Canvas Integration
 *
 * Collapsible section for the node inspector that displays and edits
 * privacy governance data: applicable laws, legal basis, retention,
 * cross-border transfers, DPIA assessments, and compliance scores.
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import {
  ShieldCheck,
  Scale,
  Clock,
  Globe,
  FileSearch,
  BarChart3,
  Plus,
  X,
  Loader2,
  Info,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import { CollapsibleSection } from '@/components/ui/collapsible-section';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { usePrivacyGovernance } from '@/hooks/usePrivacyGovernance';
import { usePermission } from '@/hooks/usePermission';
import type {
  LegalBasis,
  CrossBorderMechanism,
  DpiaStatus,
  ApplicableLaw,
} from '@/types/privacy-governance';
import {
  LEGAL_BASIS_LABELS,
  CROSS_BORDER_MECHANISM_LABELS,
  DPIA_STATUS_LABELS,
  DPIA_STATUS_COLORS,
  COUNTRY_FLAGS,
} from '@/types/privacy-governance';

interface PrivacyGovernancePanelProps {
  nodeId: string;
}

// Helper to check if a string is a valid UUID
const isValidUUID = (id: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

/** Get flag emoji for a country code, fallback to globe */
function getCountryFlag(country?: string): string {
  if (!country) return '\uD83C\uDF10';
  const code = country.toUpperCase();
  return COUNTRY_FLAGS[code] || '\uD83C\uDF10';
}

/** Get score color class based on value */
function getScoreColor(score: number): string {
  if (score > 80) return 'text-emerald-600 dark:text-emerald-400';
  if (score > 50) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

/** Get score progress color class based on value */
function getScoreProgressColor(score: number): string {
  if (score > 80) return '[&>div]:bg-emerald-500';
  if (score > 50) return '[&>div]:bg-amber-500';
  return '[&>div]:bg-red-500';
}

export function PrivacyGovernancePanel({ nodeId }: PrivacyGovernancePanelProps) {
  const isPersistedNode = isValidUUID(nodeId);
  const canUpdate = usePermission('privacy_governance', 'UPDATE');

  const {
    governance,
    loading,
    score,
    dpiaAssessment,
    applicableLaws,
    update,
  } = usePrivacyGovernance(nodeId);

  // Local state for debounced fields
  const [localPurpose, setLocalPurpose] = useState('');
  const [localRetention, setLocalRetention] = useState('');
  const [localCountry, setLocalCountry] = useState('');
  const [purposeInitialized, setPurposeInitialized] = useState(false);
  const [retentionInitialized, setRetentionInitialized] = useState(false);
  const [countryInitialized, setCountryInitialized] = useState(false);

  // Debounce timers
  const purposeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retentionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local state from governance data when it loads
  if (governance && !purposeInitialized) {
    setLocalPurpose(governance.purpose || '');
    setPurposeInitialized(true);
  }
  if (governance && !retentionInitialized) {
    setLocalRetention(governance.retentionDays?.toString() || '');
    setRetentionInitialized(true);
  }
  if (governance && !countryInitialized) {
    setLocalCountry(governance.crossBorderCountry || '');
    setCountryInitialized(true);
  }

  // Merge applicable law legal basis options into a unique set
  const allLaws = useMemo(() => {
    if (!applicableLaws) return [];
    return [...(applicableLaws.explicit || []), ...(applicableLaws.suggested || [])];
  }, [applicableLaws]);

  const legalBasisOptions = useMemo(() => {
    const optionsSet = new Set<string>();
    allLaws.forEach((law) => {
      law.legalBasisOptions?.forEach((opt) => optionsSet.add(opt));
    });
    // If no laws have basis options, show all
    if (optionsSet.size === 0) {
      return Object.keys(LEGAL_BASIS_LABELS) as LegalBasis[];
    }
    return Array.from(optionsSet) as LegalBasis[];
  }, [allLaws]);

  const requiresLegalBasis = useMemo(() => {
    return allLaws.some((law) => law.requiresLegalBasis);
  }, [allLaws]);

  const requiresRetention = useMemo(() => {
    return allLaws.some((law) => law.requiresRetentionPolicy);
  }, [allLaws]);

  // Handlers

  const handlePurposeChange = useCallback(
    (value: string) => {
      setLocalPurpose(value);
      if (purposeTimerRef.current) clearTimeout(purposeTimerRef.current);
      purposeTimerRef.current = setTimeout(() => {
        update({ purpose: value });
      }, 800);
    },
    [update]
  );

  const handleLegalBasisChange = useCallback(
    (value: string) => {
      update({ legalBasis: value as LegalBasis });
    },
    [update]
  );

  const handleRetentionChange = useCallback(
    (value: string) => {
      setLocalRetention(value);
      if (retentionTimerRef.current) clearTimeout(retentionTimerRef.current);
      retentionTimerRef.current = setTimeout(() => {
        const days = parseInt(value, 10);
        if (!isNaN(days) && days >= 0) {
          update({ retentionDays: days });
        }
      }, 800);
    },
    [update]
  );

  const handleCrossBorderToggle = useCallback(
    (checked: boolean) => {
      update({
        isCrossBorder: checked,
        ...(!checked ? { crossBorderCountry: undefined, crossBorderMechanism: undefined } : {}),
      });
    },
    [update]
  );

  const handleCrossBorderCountryChange = useCallback(
    (value: string) => {
      setLocalCountry(value);
      if (countryTimerRef.current) clearTimeout(countryTimerRef.current);
      countryTimerRef.current = setTimeout(() => {
        update({ crossBorderCountry: value });
      }, 800);
    },
    [update]
  );

  const handleCrossBorderMechanismChange = useCallback(
    (value: string) => {
      update({ crossBorderMechanism: value as CrossBorderMechanism });
    },
    [update]
  );

  const handleDpiaStatusChange = useCallback(
    (value: string) => {
      update({ dpiaStatus: value as DpiaStatus });
    },
    [update]
  );

  const handleRemoveLaw = useCallback(
    (lawId: string) => {
      if (!governance) return;
      const current = governance.applicableLawIds || [];
      update({ applicableLawIds: current.filter((id) => id !== lawId) });
    },
    [governance, update]
  );

  const handleAddLaw = useCallback(
    (lawId: string) => {
      if (!governance) return;
      const current = governance.applicableLawIds || [];
      if (!current.includes(lawId)) {
        update({ applicableLawIds: [...current, lawId] });
      }
    },
    [governance, update]
  );

  // Compute explicit + suggested laws that are not yet applied
  const unappliedLaws = useMemo(() => {
    if (!applicableLaws || !governance) return [];
    const applied = new Set(governance.applicableLawIds || []);
    const all = [...(applicableLaws.explicit || []), ...(applicableLaws.suggested || [])];
    return all.filter((law) => !applied.has(law.id));
  }, [applicableLaws, governance]);

  // Find law details by id
  const getLawById = useCallback(
    (lawId: string): ApplicableLaw | undefined => {
      return allLaws.find((l) => l.id === lawId);
    },
    [allLaws]
  );

  // Score badge for header
  const scoreBadge = useMemo(() => {
    if (!score) return null;
    const total = score.total;
    const colorClass = getScoreColor(total);
    return (
      <Badge variant="outline" className={cn('text-xs px-1.5 py-0 font-semibold', colorClass)}>
        {total}%
      </Badge>
    );
  }, [score]);

  return (
    <CollapsibleSection
      id={`${nodeId}-privacy-governance`}
      title="Privacidad y Cumplimiento"
      icon={ShieldCheck}
      defaultOpen={false}
      badge={scoreBadge}
    >
      <div className="space-y-4">
        {/* Loading State */}
        {loading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            Cargando datos de privacidad...
          </div>
        )}

        {/* Unsaved Node Warning */}
        {!loading && !isPersistedNode && (
          <div className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/20 dark:text-amber-400 p-2 rounded-md text-center">
            Guarda el workflow para configurar gobernanza de privacidad.
          </div>
        )}

        {/* Main Content */}
        {!loading && isPersistedNode && governance && (
          <>
            {/* 1. Applicable Laws */}
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1.5 text-muted-foreground">
                <Scale className="h-3 w-3" />
                Leyes Aplicables
              </Label>

              {/* Applied Laws Chips */}
              <div className="flex flex-wrap gap-1.5">
                {(governance.applicableLawIds || []).map((lawId) => {
                  const law = getLawById(lawId);
                  if (!law) return null;
                  return (
                    <Badge
                      key={lawId}
                      variant="secondary"
                      className="text-xs gap-1 pl-1.5 pr-1"
                    >
                      <span>{getCountryFlag(law.country)}</span>
                      <span>{law.code}</span>
                      {canUpdate && (
                        <button
                          type="button"
                          onClick={() => handleRemoveLaw(lawId)}
                          className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      )}
                    </Badge>
                  );
                })}

                {(governance.applicableLawIds || []).length === 0 && (
                  <span className="text-xs text-muted-foreground italic">
                    Sin leyes asignadas
                  </span>
                )}
              </div>

              {/* Suggested Laws / Add Button */}
              {canUpdate && unappliedLaws.length > 0 && (
                <Select onValueChange={handleAddLaw}>
                  <SelectTrigger className="h-7 text-xs">
                    <div className="flex items-center gap-1.5">
                      <Plus className="h-3 w-3" />
                      <SelectValue placeholder="Agregar ley..." />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {unappliedLaws.map((law) => (
                      <SelectItem key={law.id} value={law.id} className="text-xs">
                        <div className="flex items-center gap-1.5">
                          <span>{getCountryFlag(law.country)}</span>
                          <span className="font-medium">{law.code}</span>
                          <span className="text-muted-foreground">- {law.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* 2. Purpose */}
            <div className="space-y-1.5">
              <Label htmlFor={`${nodeId}-purpose`} className="text-xs flex items-center gap-1.5 text-muted-foreground">
                <Info className="h-3 w-3" />
                Proposito del Tratamiento
              </Label>
              <Textarea
                id={`${nodeId}-purpose`}
                value={localPurpose}
                onChange={(e) => handlePurposeChange(e.target.value)}
                placeholder="Describe el proposito del tratamiento de datos personales..."
                className="min-h-[50px] text-xs resize-none"
                disabled={!canUpdate}
              />
            </div>

            {/* 3. Legal Basis - only if applicable laws require it */}
            {requiresLegalBasis && (
              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-1.5 text-muted-foreground">
                  <Scale className="h-3 w-3" />
                  Base Legal
                </Label>
                <RadioGroup
                  value={governance.legalBasis || ''}
                  onValueChange={handleLegalBasisChange}
                  disabled={!canUpdate}
                  className="space-y-1"
                >
                  {legalBasisOptions.map((basis) => (
                    <div key={basis} className="flex items-center space-x-2">
                      <RadioGroupItem value={basis} id={`${nodeId}-basis-${basis}`} className="h-3.5 w-3.5" />
                      <Label htmlFor={`${nodeId}-basis-${basis}`} className="text-xs font-normal cursor-pointer">
                        {LEGAL_BASIS_LABELS[basis] || basis}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* 4. Retention - only if applicable laws require it */}
            {requiresRetention && (
              <div className="space-y-1.5">
                <Label htmlFor={`${nodeId}-retention`} className="text-xs flex items-center gap-1.5 text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  Periodo de Retencion
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id={`${nodeId}-retention`}
                    type="number"
                    min={0}
                    value={localRetention}
                    onChange={(e) => handleRetentionChange(e.target.value)}
                    placeholder="0"
                    className="h-7 w-24 text-xs"
                    disabled={!canUpdate}
                  />
                  <span className="text-xs text-muted-foreground">dias</span>
                </div>
              </div>
            )}

            {/* 5. Cross-Border Transfer */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs flex items-center gap-1.5 text-muted-foreground">
                  <Globe className="h-3 w-3" />
                  Transferencia Transfronteriza
                </Label>
                <Switch
                  checked={governance.isCrossBorder || false}
                  onCheckedChange={handleCrossBorderToggle}
                  disabled={!canUpdate}
                  className="scale-75"
                />
              </div>

              {governance.isCrossBorder && (
                <div className="space-y-2 pl-1 border-l-2 border-muted ml-1">
                  <div className="space-y-1 pl-2">
                    <Label htmlFor={`${nodeId}-cb-country`} className="text-[11px] text-muted-foreground">
                      Pais destino
                    </Label>
                    <Input
                      id={`${nodeId}-cb-country`}
                      type="text"
                      value={localCountry}
                      onChange={(e) => handleCrossBorderCountryChange(e.target.value)}
                      placeholder="Codigo ISO (ej: US, EU, BR)"
                      className="h-7 text-xs"
                      disabled={!canUpdate}
                    />
                  </div>
                  <div className="space-y-1 pl-2">
                    <Label htmlFor={`${nodeId}-cb-mechanism`} className="text-[11px] text-muted-foreground">
                      Mecanismo de transferencia
                    </Label>
                    <Select
                      value={governance.crossBorderMechanism || ''}
                      onValueChange={handleCrossBorderMechanismChange}
                      disabled={!canUpdate}
                    >
                      <SelectTrigger className="h-7 text-xs">
                        <SelectValue placeholder="Seleccionar mecanismo..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.entries(CROSS_BORDER_MECHANISM_LABELS) as [CrossBorderMechanism, string][]).map(
                          ([value, label]) => (
                            <SelectItem key={value} value={value} className="text-xs">
                              {label}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            {/* 6. DPIA Assessment */}
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1.5 text-muted-foreground">
                <FileSearch className="h-3 w-3" />
                Evaluacion de Impacto (DPIA)
              </Label>

              {/* DPIA Required Badge */}
              {dpiaAssessment && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {dpiaAssessment.required ? (
                      <Badge className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-100">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        DPIA Requerida
                      </Badge>
                    ) : (
                      <Badge className="text-xs bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-100">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        DPIA No Requerida
                      </Badge>
                    )}
                  </div>

                  {/* Reasons */}
                  {dpiaAssessment.required && dpiaAssessment.reasons.length > 0 && (
                    <div className="space-y-1 pl-1">
                      {dpiaAssessment.reasons.map((reason, idx) => (
                        <p key={idx} className="text-[11px] text-muted-foreground flex items-start gap-1">
                          <span className="text-amber-500 mt-0.5">*</span>
                          {reason}
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Triggering Laws */}
                  {dpiaAssessment.required && dpiaAssessment.triggeringLaws.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {dpiaAssessment.triggeringLaws.map((law) => (
                        <Badge key={law.id} variant="outline" className="text-[10px] px-1 py-0">
                          {law.code}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* DPIA Status Selector (only if required) */}
                  {dpiaAssessment.required && (
                    <div className="space-y-1">
                      <Label className="text-[11px] text-muted-foreground">Estado</Label>
                      <Select
                        value={governance.dpiaStatus || 'pending'}
                        onValueChange={handleDpiaStatusChange}
                        disabled={!canUpdate}
                      >
                        <SelectTrigger className="h-7 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(
                            Object.entries(DPIA_STATUS_LABELS).filter(
                              ([key]) => key !== 'not_required'
                            ) as [DpiaStatus, string][]
                          ).map(([value, label]) => (
                            <SelectItem key={value} value={value} className="text-xs">
                              <div className="flex items-center gap-1.5">
                                <span
                                  className={cn(
                                    'inline-block h-2 w-2 rounded-full',
                                    value === 'pending' && 'bg-amber-500',
                                    value === 'in_progress' && 'bg-blue-500',
                                    value === 'completed' && 'bg-emerald-500'
                                  )}
                                />
                                {label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 7. Compliance Score */}
            {score && (
              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-1.5 text-muted-foreground">
                  <BarChart3 className="h-3 w-3" />
                  Puntaje de Cumplimiento
                </Label>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className={cn('text-lg font-bold', getScoreColor(score.total))}>
                      {score.total}%
                    </span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="text-muted-foreground hover:text-foreground">
                            <Info className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-[220px]">
                          <div className="space-y-1 text-xs">
                            <p className="font-semibold mb-1">Desglose del Puntaje</p>
                            <ScoreRow label="Clasificacion" value={score.breakdown.classification} />
                            <ScoreRow label="Proposito" value={score.breakdown.purpose} />
                            <ScoreRow label="Base Legal" value={score.breakdown.legalBasis} />
                            <ScoreRow label="Retencion" value={score.breakdown.retention} />
                            <ScoreRow label="Atributos" value={score.breakdown.attributesClassified} />
                            <ScoreRow label="DPIA" value={score.breakdown.dpia} />
                            <ScoreRow label="Leyes" value={score.breakdown.laws} />
                            {((score.penalties?.dpiaNotCompleted ?? 0) > 0 || (score.penalties?.crossBorderNoMechanism ?? 0) > 0) && (
                              <>
                                <p className="font-semibold mt-1 text-red-500">Penalidades</p>
                                {(score.penalties?.dpiaNotCompleted ?? 0) > 0 && (
                                  <ScoreRow label="DPIA incompleta" value={-(score.penalties?.dpiaNotCompleted ?? 0)} isNegative />
                                )}
                                {(score.penalties?.crossBorderNoMechanism ?? 0) > 0 && (
                                  <ScoreRow label="Sin mecanismo" value={-(score.penalties?.crossBorderNoMechanism ?? 0)} isNegative />
                                )}
                              </>
                            )}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  <Progress
                    value={score.total}
                    className={cn('h-2', getScoreProgressColor(score.total))}
                  />

                  {/* Quick Status Row */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {score.total > 80 && (
                      <Badge className="text-[10px] px-1.5 py-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 hover:bg-emerald-100">
                        Cumple
                      </Badge>
                    )}
                    {score.total > 50 && score.total <= 80 && (
                      <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-100">
                        Parcial
                      </Badge>
                    )}
                    {score.total <= 50 && (
                      <Badge className="text-[10px] px-1.5 py-0 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-100">
                        No Cumple
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Read-only notice */}
            {!canUpdate && (
              <p className="text-[10px] text-muted-foreground italic text-center pt-1">
                No tienes permisos para editar la gobernanza de privacidad.
              </p>
            )}
          </>
        )}
      </div>
    </CollapsibleSection>
  );
}

/** Small helper component for score breakdown rows */
function ScoreRow({
  label,
  value,
  isNegative = false,
}: {
  label: string;
  value: number;
  isNegative?: boolean;
}) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span className={cn('font-medium', isNegative ? 'text-red-500' : 'text-foreground')}>
        {isNegative ? value : `+${value}`}
      </span>
    </div>
  );
}
